import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { SEOHead } from '@/components/atoms/SEOHead';
import { TagBadge, type TagCategory } from '@/components/atoms/TagBadge';
import { cn } from '@/lib/cn';

/**
 * 태그 상세 페이지 — Phase 7
 *   /tag/:slug — 해당 태그 부여된 종목 리스트 (시총순)
 */

interface TickerInTag {
  ticker: string;
  name: string;
  market: string | null;
  sectorSlug: string | null;
  price: number | null;
  changeRate: number | null;
  marketCap: number | null;
  source: 'rule' | 'ai' | 'manual';
  confidence: number;
}

interface TagPageData {
  tag: {
    slug: string;
    name: string;
    category: string;
    description: string | null;
    tickerCount: number;
  };
  tickers: TickerInTag[];
}

const CATEGORY_KO: Record<string, string> = {
  style: '스타일',
  size: '규모',
  sector: '섹터',
  theme: '테마',
  trait: '특성',
  risk: '리스크',
  valuechain: '밸류체인',
};

function fmtMarketCap(v: number | null): string {
  if (v == null) return '-';
  /* 원 단위 → 조/억 한글 표기 */
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}조원`;
  if (v >= 1e8) return `${(v / 1e8).toFixed(0)}억원`;
  return v.toLocaleString('ko-KR') + '원';
}

function fmtPrice(v: number | null): string {
  if (v == null) return '-';
  return v.toLocaleString('ko-KR') + '원';
}

function fmtChange(v: number | null): string {
  if (v == null) return '-';
  const s = v > 0 ? '+' : '';
  return `${s}${v.toFixed(2)}%`;
}

function changeCls(v: number | null): string {
  if (v == null || v === 0) return 'text-[var(--color-text-sub)]';
  return v > 0 ? 'text-red-500' : 'text-blue-500';
}

type SortKey = 'name' | 'market' | 'price' | 'changeRate' | 'marketCap';
type SortDir = 'asc' | 'desc';

export function TagPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tags', slug],
    queryFn: () => get<TagPageData>(`/tags/${slug}`),
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });

  /* 정렬 — 기본은 시총 내림차순 (서버 응답 순서 그대로) */
  const [sortKey, setSortKey] = useState<SortKey>('marketCap');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sortedTickers = useMemo(() => {
    const list = [...(data?.tickers ?? [])];
    const cmp = (a: TickerInTag, b: TickerInTag) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      /* null/undefined 는 항상 끝으로 */
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      return sortDir === 'asc'
        ? sa.localeCompare(sb, 'ko')
        : sb.localeCompare(sa, 'ko');
    };
    return list.sort(cmp);
  }, [data?.tickers, sortKey, sortDir]);

  const onSortClick = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      /* 숫자 컬럼은 기본 desc, 문자열 컬럼은 기본 asc */
      setSortDir(['price', 'changeRate', 'marketCap'].includes(key) ? 'desc' : 'asc');
    }
  };

  if (isLoading) {
    return (
      <div className="container-base py-6">
        <Skeleton className="h-24 w-full mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container-base py-16 text-center">
        <p className="text-sm text-[var(--color-text-sub)]">
          존재하지 않거나 비활성 상태인 태그입니다.
        </p>
        <Link
          to="/tags"
          className="mt-4 inline-block text-sm text-[var(--color-primary)] hover:underline"
        >
          전체 태그 보기 →
        </Link>
      </div>
    );
  }

  const { tag, tickers } = data;
  const categoryKo = CATEGORY_KO[tag.category] ?? tag.category;
  const seoTitle = `${tag.name} 관련 종목 ${tag.tickerCount}개`;
  const seoDescription =
    tag.description
      ? `${tag.description}. ${tag.name} 관련 종목 ${tag.tickerCount}개를 시총순으로 확인하세요.`
      : `${tag.name} 관련 종목 ${tag.tickerCount}개를 시총순으로 확인하세요.`;

  return (
    <div className="container-base py-6">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={`/tag/${tag.slug}`}
      />

      {/* 헤더 */}
      <header className="mb-6 rounded-2xl border border-[var(--color-border)] bg-white p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-[var(--color-text-sub)]">
            {categoryKo}
          </span>
          <TagBadge
            slug={tag.slug}
            name={tag.name}
            category={tag.category as TagCategory}
            asLink={false}
          />
        </div>
        <h1 className="mt-2 text-2xl font-extrabold md:text-3xl">{tag.name}</h1>
        {tag.description && (
          <p className="mt-1 text-sm text-[var(--color-text-sub)]">{tag.description}</p>
        )}
        <p className="mt-2 text-xs text-[var(--color-text-sub)]">
          관련 종목 <span className="font-semibold text-[var(--color-text)]">{tag.tickerCount}</span>개
          <span className="ml-2">· 시가총액 내림차순 정렬</span>
        </p>
      </header>

      {/* 종목 리스트 */}
      {tickers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
          현재 이 태그에 부여된 종목이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] text-left text-xs text-[var(--color-text-sub)]">
              <tr>
                <th className="p-3 font-medium">#</th>
                <SortableTh label="종목" k="name" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
                <SortableTh label="시장" k="market" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
                <SortableTh label="현재가" k="price" align="right" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
                <SortableTh label="등락률" k="changeRate" align="right" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
                <SortableTh label="시총" k="marketCap" align="right" sortKey={sortKey} sortDir={sortDir} onClick={onSortClick} />
              </tr>
            </thead>
            <tbody>
              {sortedTickers.map((t, i) => (
                <tr
                  key={t.ticker}
                  className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg)]"
                >
                  <td className="p-3 text-xs text-[var(--color-text-sub)] tabular-nums">
                    {i + 1}
                  </td>
                  <td className="p-3">
                    <Link
                      to={`/ticker/${t.ticker}`}
                      className="font-semibold hover:text-[var(--color-primary)]"
                    >
                      {t.name}
                    </Link>
                    <span className="ml-1.5 font-mono text-[10px] text-[var(--color-text-sub)]">
                      {t.ticker}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-[var(--color-text-sub)]">
                    {t.market ?? '-'}
                  </td>
                  <td className="p-3 text-right tabular-nums">{fmtPrice(t.price)}</td>
                  <td
                    className={cn(
                      'p-3 text-right tabular-nums font-semibold',
                      changeCls(t.changeRate),
                    )}
                  >
                    {fmtChange(t.changeRate)}
                  </td>
                  <td className="p-3 text-right tabular-nums text-[var(--color-text-sub)]">
                    {fmtMarketCap(t.marketCap)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* 정렬 가능한 테이블 헤더 셀 */
function SortableTh({
  label,
  k,
  align = 'left',
  sortKey,
  sortDir,
  onClick,
}: {
  label: string;
  k: SortKey;
  align?: 'left' | 'right';
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th className={cn('p-0 font-medium', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={() => onClick(k)}
        className={cn(
          'flex w-full items-center gap-0.5 p-3 transition-colors hover:bg-[var(--color-border)]/40',
          align === 'right' && 'justify-end',
          active && 'text-[var(--color-primary)]',
        )}
        title={`${label} ${active ? (sortDir === 'asc' ? '오름차순' : '내림차순') : '정렬'}`}
      >
        <span>{label}</span>
        <span className="text-[10px] opacity-60">
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    </th>
  );
}
